import React, { useEffect, useRef, useState } from 'react';
import { Box, FormHelperText } from '@mui/material';
import $ from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';

/**
 * Select2Dropdown Component
 * A React wrapper for Select2 with Bootstrap theme and async data loading support
 * 
 * @param {Object} props
 * @param {Array} props.defaultOptions - Default options to show initially
 * @param {Function} props.loadOptions - Function to load options (async, receives searchText, pageNumber, pageSize)
 * @param {string|number} props.value - Selected value
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.label - Label for the dropdown
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.getOptionLabel - Function to get label from option
 * @param {Function} props.getOptionValue - Function to get value from option
 * @param {boolean} props.disabled - Whether dropdown is disabled
 * @param {number} props.pageSize - Number of items to load per page (default: 20)
 * @param {number} props.minSearchLength - Minimum characters before searching (default: 0)
 * @param {boolean} props.loadOnMount - Whether to load options on component mount (default: true)
 */
const Select2Dropdown = ({
  defaultOptions = [],
  loadOptions,
  value,
  onChange,
  label,
  required = false,
  placeholder = 'Select an option...',
  getOptionLabel = (option) => option?.label || option?.name || option?.MarketName || String(option),
  getOptionValue = (option) => option?.value || option?.id || option?.MarketId || option,
  disabled = false,
  pageSize = 20,
  minSearchLength = 0,
  loadOnMount = true,
  allowClear = false,
}) => {
  const selectRef = useRef(null);
  const select2Instance = useRef(null);
  const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);

  // Initialize Select2
  useEffect(() => {
    if (!selectRef.current) return;

    const $select = $(selectRef.current);
    
    // Prepare initial options from defaultOptions
    const initialOptions = defaultOptions.map((option) => {
      const optionValue = getOptionValue(option);
      const optionLabel = getOptionLabel(option);
      return new Option(optionLabel, optionValue, false, false);
    });
    
    // Add initial options to select
    $select.empty();
    if (!required) {
      $select.append(new Option(placeholder, '', true, true));
    }
    initialOptions.forEach(opt => $select.append(opt));
    
    // Initialize Select2 with Bootstrap theme (via CSS)
    const select2Config = {
      placeholder: placeholder,
      allowClear: allowClear && !required,
      width: '100%',
    };

    // Add AJAX if loadOptions is provided
    if (loadOptions) {
      select2Config.ajax = {
        delay: 300,
        transport: function(params, success, failure) {
          const search = params.data.term || '';
          const page = params.data.page || 1;
          
          // Check minimum search length
          if (search.length < minSearchLength && search.length > 0) {
            success({ results: [], pagination: { more: false } });
            return;
          }

          loadOptions(search, page, pageSize)
            .then((response) => {
              const data = response.data || response || [];
              const totalCount = response.totalCount || data.length;
              const hasMoreData = page * pageSize < totalCount;

              // Format options for Select2
              const formattedResults = data.map((option) => ({
                id: String(getOptionValue(option)),
                text: getOptionLabel(option),
              }));

              success({
                results: formattedResults,
                pagination: {
                  more: hasMoreData,
                },
              });
            })
            .catch((error) => {
              console.error('Error loading options:', error);
              failure(error);
            });
        },
        data: function(params) {
          return {
            term: params.term || '',
            page: params.page || 1,
          };
        },
        processResults: function(data) {
          return data;
        },
      };
    }

    select2Instance.current = $select.select2(select2Config);

    // Apply Material-UI-like styling
    const $container = $select.next('.select2-container');
    $container.addClass('mui-select2-container');
    
    // Style the selection area to match Material-UI TextField
    const $selection = $container.find('.select2-selection');
    const updateSelectionPadding = () => {
      const hasValueOrFocused = $select.val() || $container.hasClass('select2-container--open');
      $selection.css({
        'min-height': '56px',
        'border': '1px solid rgba(0, 0, 0, 0.23)',
        'border-radius': '4px',
        'background-color': '#ffffff',
        'padding-top': hasValueOrFocused ? '20px' : '14px',
        'padding-bottom': hasValueOrFocused ? '6px' : '14px',
        'padding-left': '14px',
        'padding-right': '14px',
        'display': 'flex',
        'align-items': 'center',
      });
    };
    
    updateSelectionPadding();

    // Style the selection rendered
    const $selectionRendered = $container.find('.select2-selection__rendered');
    $selectionRendered.css({
      'padding': '0',
      'line-height': '1.4375em',
      'color': 'rgba(0, 0, 0, 0.87)',
    });

    // Style placeholder
    const $placeholder = $container.find('.select2-selection__placeholder');
    $placeholder.css({
      'color': 'rgba(0, 0, 0, 0.6)',
      'margin': '0',
    });

    // Style arrow
    const $arrow = $container.find('.select2-selection__arrow');
    $arrow.css({
      'height': '100%',
      'right': '14px',
    });

    // Handle focus styles and label positioning
    $select.on('select2:open', function() {
      setIsFocused(true);
      updateSelectionPadding();
      $selection.css({
        'border-color': '#1976d2',
        'border-width': '2px',
      });
    });

    $select.on('select2:close', function() {
      setIsFocused(false);
      const currentVal = $select.val();
      setHasValue(!!currentVal);
      updateSelectionPadding();
      if (!$select.val()) {
        $selection.css({
          'border-color': 'rgba(0, 0, 0, 0.23)',
          'border-width': '1px',
        });
      } else {
        $selection.css({
          'border-color': 'rgba(0, 0, 0, 0.23)',
          'border-width': '1px',
        });
      }
    });

    // Handle selection change
    $select.on('select2:select select2:clear', function(e) {
      const selectedValue = $select.val();
      setHasValue(!!selectedValue);
      updateSelectionPadding();
      if (onChange) {
        onChange(selectedValue || null);
      }
      // Update border on selection
      if (selectedValue) {
        $selection.css({
          'border-color': 'rgba(0, 0, 0, 0.23)',
          'border-width': '1px',
        });
      }
    });

    // Set initial value if provided
    if (value !== null && value !== undefined && value !== '') {
      $select.val(String(value)).trigger('change');
      setHasValue(true);
    } else {
      setHasValue(false);
    }

    setInitialOptionsLoaded(true);

    // Cleanup
    return () => {
      if (select2Instance.current) {
        select2Instance.current.off('select2:select select2:clear');
        select2Instance.current.select2('destroy');
        select2Instance.current = null;
      }
    };
  }, []); // Only run on mount

  // Update value when prop changes
  useEffect(() => {
    if (select2Instance.current && initialOptionsLoaded && value !== undefined) {
      const $select = $(selectRef.current);
      const $container = $select.next('.select2-container');
      const $selection = $container.find('.select2-selection');
      const currentValue = $select.val();
      const newValue = value ? String(value) : null;
      
      if (currentValue !== newValue) {
        $select.val(newValue).trigger('change');
        setHasValue(!!newValue);
        
        // Update padding based on value
        if (newValue) {
          $selection.css({
            'padding-top': '20px',
            'padding-bottom': '6px',
          });
        } else {
          $selection.css({
            'padding-top': '14px',
            'padding-bottom': '14px',
          });
        }
      }
    }
  }, [value, initialOptionsLoaded]);

  // Update disabled state
  useEffect(() => {
    if (select2Instance.current && initialOptionsLoaded) {
      const $select = $(selectRef.current);
      if (disabled) {
        $select.prop('disabled', true);
      } else {
        $select.prop('disabled', false);
      }
    }
  }, [disabled, initialOptionsLoaded]);

  // Update options when defaultOptions changes
  useEffect(() => {
    if (select2Instance.current && initialOptionsLoaded) {
      const $select = $(selectRef.current);
      const currentValue = $select.val();
      const targetValue = value ? String(value) : null;
      
      // Clear existing options
      $select.empty();
      
      // Add placeholder option if not required
      if (!required) {
        $select.append(new Option(placeholder, '', false, false));
      }
      
      // Add all options from defaultOptions
      defaultOptions.forEach((option) => {
        const optionValue = String(getOptionValue(option));
        const optionLabel = getOptionLabel(option);
        const isSelected = targetValue && targetValue === optionValue;
        $select.append(new Option(optionLabel, optionValue, isSelected, isSelected));
      });
      
      // Re-set the target value (from prop) if it exists in the new options
      if (targetValue) {
        const valueExists = defaultOptions.some(opt => String(getOptionValue(opt)) === targetValue);
        if (valueExists) {
          $select.val(targetValue).trigger('change.select2');
          setHasValue(true);
        } else {
          $select.val(null).trigger('change.select2');
          setHasValue(false);
        }
      } else if (currentValue) {
        // Fallback: try to preserve current value if no prop value is set
        const valueExists = defaultOptions.some(opt => String(getOptionValue(opt)) === String(currentValue));
        if (valueExists) {
          $select.val(currentValue).trigger('change.select2');
          setHasValue(true);
        } else {
          $select.val(null).trigger('change.select2');
          setHasValue(false);
        }
      }
    }
  }, [defaultOptions, initialOptionsLoaded, getOptionLabel, getOptionValue, placeholder, required, value]);

  return (
    <Box
      className={!allowClear ? 'select2-no-clear' : ''}
      sx={{
        width: '100%',
        position: 'relative',
        '& .mui-select2-container': {
          '& .select2-selection': {
            minHeight: '56px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            backgroundColor: '#ffffff',
            padding: '14px 14px',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
            },
          },
          '&.select2-container--focus .select2-selection': {
            borderColor: '#1976d2',
            borderWidth: '2px',
          },
          '& .select2-selection__rendered': {
            padding: '0',
            lineHeight: '1.4375em',
            color: 'rgba(0, 0, 0, 0.87)',
          },
          '& .select2-selection__placeholder': {
            color: 'rgba(0, 0, 0, 0.6)',
            margin: '0',
          },
          '& .select2-selection__arrow': {
            height: '100%',
            right: '14px',
          },
        },
      }}
    >
      {label && (
        <Box
          component="label"
          sx={{
            position: 'absolute',
            top: (hasValue || isFocused) ? '-9px' : '16px',
            left: '14px',
            fontSize: (hasValue || isFocused) ? '0.75rem' : '1rem',
            color: isFocused ? '#1976d2' : 'rgba(0, 0, 0, 0.6)',
            pointerEvents: 'none',
            transform: (hasValue || isFocused) ? 'translate(0, 0) scale(0.75)' : 'translate(0, 0) scale(1)',
            transformOrigin: 'top left',
            transition: 'all 0.2s',
            zIndex: 1,
            backgroundColor: '#ffffff',
            padding: (hasValue || isFocused) ? '0 4px' : '0',
            '&::after': {
              content: required ? '" *"' : '""',
              color: 'red',
            },
          }}
        >
          {label}
        </Box>
      )}
      <select
        ref={selectRef}
        style={{
          width: '100%',
          height: '56px',
        }}
        disabled={disabled}
      />
      <style>{`
        .select2-dropdown {
          border: 1px solid rgba(0, 0, 0, 0.23);
          border-radius: 4px;
          box-shadow: 0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12);
        }
        .select2-search--dropdown .select2-search__field {
          border: 1px solid rgba(0, 0, 0, 0.23);
          border-radius: 4px;
          padding: 8px 12px;
        }
        .select2-results__option {
          padding: 8px 16px;
        }
        .select2-results__option--highlighted {
          background-color: rgba(25, 118, 210, 0.08);
        }
        .select2-results__option[aria-selected="true"] {
          background-color: rgba(25, 118, 210, 0.12);
        }
        .select2-no-clear .select2-selection__clear {
          display: none !important;
        }
      `}</style>
    </Box>
  );
};

export default Select2Dropdown;

